import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import styled from 'styled-components';
import { useSearchContext } from '../../../pages/pesquisa/hooks/useSearchContext';
import { useSearchFilter } from '../../../pages/pesquisa/hooks/useSearchFilter';
import { VideoFiltersContext } from '../../../pages/v2/video/VideoFiltersContext';
import { BookFiltersContext } from '../../../pages/v2/book/BookFiltersContext';
import { PeriodicFiltersContext } from '../../../pages/v2/periodic/PeriodicFiltersContext';
import { CodesFiltersContext } from '../../../pages/v2/codes/CodesFiltersContext';
import {
  Container,
  DivDot,
  GlobalNavBarAdvanced,
  SpanDotGreen,
  SpanDotPink,
  Wrapper
} from './styles';
import { NavbarContext, SearchScopeType } from '../NavbarFilters/NavbarContext';
import { useQuery } from '../../../pages/v2/_shared/useQuery';
import { SearchFilter } from '../../../models/search/SearchFilter';
import {
  bookRoutes,
  getActivePath,
  getUrlParamsForSearch,
  getValidSortBy,
  goTo,
  ignoreSearchOnFirstAccess,
  periodicRoutes,
  videoRoutes,
  periodicRoutesV2,
  codesRoutesV2,
  informativesRoutesV2
} from '../../v2/searchHelpers';
import { GlobalAdvancedFilter } from './GlobalAdvancedFilter';
import { GlobalFiltersContext } from '../../../pages/v2/home/GlobalFiltersContext';
import { Icon, Popover, Select } from 'antd';
import { InformativesFiltersContext } from '../../../pages/v2/informatives/InformativesFiltersContext';

const { Option } = Select;

const SearchBreadth = styled.div`
  font-size: 12px;
  padding-top: 5px;
  margin-left: 20px;
  @media (max-width: 991px) {
    margin-top: 10px;
    margin-left: 0;
  }
`;

const SpanDivider = styled.div`
  margin-left: 20px;
  font-size: 25px;
  @media (max-width: 991px) {
    display: none;
  }
`;

const SpanClearAll = styled.div`
  margin-left: 20px;
  margin-top: 10px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  @media (max-width: 991px) {
    display: none;
  }
`;

const SpanClearAllInternal = styled.div`
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  float: right;
  margin-right: 50px;
  @media (min-width: 991px) {
    display: none;
  }
`;

export const processAdvancedSearch = (
  search: string = '',
  searchInTitle: string | number = '0'
) => {
  const results = [];
  const andOr = ['AND', 'OR'];
  const and = 'AND';
  const not = 'NOT';
  const isDelimiter = (val: string) => [' and ', ' or ', ' not '].includes(val);
  const advDataSplit = search
    .trim()
    .toLowerCase()
    .replace(/^(and\s|or\s|not\s)/g, '')
    .replace(/(\sand|\sor|\snot)$/g, '')
    .split(/(\sand\s|\sor\s|\snot\s)/g);

  advDataSplit.forEach((val: string, ind: number, arr: any[]) => {
    if (isDelimiter(val)) return;

    const exact = false;
    const prev = arr[ind - 1];
    const lastResult = results[results.length - 1];
    const prevIsDelimiter = ind > 0 && isDelimiter(prev);
    const type = prevIsDelimiter ? prev.trim().toUpperCase() : and;
    const text = val.trim();

    if (ind === 0 || !lastResult) {
      return results.push({
        type,
        text,
        exact,
        searchInTitle
      });
    }

    if (andOr.includes(type) && andOr.includes(lastResult.type)) {
      return (lastResult.text = `${lastResult.text} ${text}`);
    } else if (type === not && lastResult.type === not) {
      return (lastResult.text = `${lastResult.text} ${text}`);
    }

    results.push({ type, text, exact, searchInTitle });
  });

  return results;
};

export const NavbarSearch = () => {
  const {
    REACT_APP_NEW_BOOK_VERSION,
    REACT_APP_NEW_PERIODIC_VERSION,
    REACT_APP_NEW_CODES_VERSION,
    REACT_APP_NEW_INFORMATIVES_VERSION
  } = process.env;
  const { filter } = useSearchFilter();
  const { changeFilter } = useSearchContext();

  const location = useLocation();
  const history = useHistory();
  const searchParam = useQuery().get('search');
  const wordsParam = useQuery().get('words');
  const scopeParam = useQuery().get('searchScope');
  const sortParam = useQuery().get('sortBy');
  const isNewBookVers = String(REACT_APP_NEW_BOOK_VERSION) === 'true';
  const isNewPeriodicVers = String(REACT_APP_NEW_PERIODIC_VERSION) === 'true';
  const isNewCodesVers = String(REACT_APP_NEW_CODES_VERSION) === 'true';
  const isNewInformativesVers =
    String(REACT_APP_NEW_INFORMATIVES_VERSION) === 'true';

  const {
    loadingGlobal,
    updateData: updateGlobalData,
    setTypeSearchValue: setGlobalTypeSearchValue,
    setAreaSearchValue: setGlobalAreaSearchValue,
    setAuthorSearchValue: setGlobalAuthorSearchValue,
    setTypeData: setGlobalTypeData,
    setAreaData: setGlobalAreaData,
    typeData: typeGlobalData,
    areaData: areaGlobalData,
    setAuthorData: setGlobalAuthorData,
    authorData: authorGlobalData,
    setDateData: setGlobalDateData,
    dateData: dateGlobalData,
    advancedData: advancedGlobalData,
    setAdvancedData: setAdvancedGlobalData,
    setEventData: setEventGlobalData,
    setAppTypeData: setAppTypeGlobalData,
    setBookData: setBookGlobalData,
    setPeriodicData: setPeriodicGlobalData,
    setSerieBookData: setSerieBookGlobalData,
    setSerieVideoData: setSerieVideoGlobalData,

    eventData: eventGlobalData,
    appTypeData: appTypeGlobalData,
    bookData: bookGlobalData,
    periodicData: periodicGlobalData,
    serieBookData: serieBookGlobalData,
    serieVideoData: serieVideoGlobalData,

    setOrder: setGlobalOrder,
    setPage: setGlobalPage
  } = useContext(GlobalFiltersContext);

  const {
    searchCount,
    searchString,
    searchScope,
    searchOpen,
    setSearchCount,
    setSearchString,
    setSearchScope
  } = useContext(NavbarContext);

  const {
    updateData: updateVideoData,
    setOrder: setVideoOrder,
    setPage: setVideoPage,
    setAreaSearchValue: setVideoAreaSearchValue,
    setAuthorSearchValue: setVideoAuthorSearchValue,
    setEventSearchValue: setVideoEventSearchValue,
    setSerieSearchValue: setVideoSerieSearchValue,
    setAreaData: setVideoAreaData,
    setAuthorData: setVideoAuthorData,
    setEventData: setVideoEventData,
    setSerieData: setVideoSerieData,
    setDateData: setVideoDateData,
    areaData: areaVideoData,
    authorData: authorVideoData,
    eventData: eventVideoData,
    serieData: serieVideoData,
    dateData: dateVideoData
  } = useContext(VideoFiltersContext);

  const {
    updateData: updateBookData,
    setOrder: setBookOrder,
    setPage: setBookPage,
    setAreaSearchValue: setBookAreaSearchValue,
    setAuthorSearchValue: setBookAuthorSearchValue,
    setSerieSearchValue: setBookSerieSearchValue,
    setAreaData: setBookAreaData,
    setAuthorData: setBookAuthorData,
    setSerieData: setBookSerieData,
    setDateData: setBookDateData,
    areaData: areaBookData,
    authorData: authorBookData,
    serieData: serieBookData,
    dateData: dateBookData
  } = useContext(BookFiltersContext);

  const {
    updateData: updatePeriodicData,
    setAreaSearchValue: setPeriodicAreaSearchValue,
    setAuthorSearchValue: setPeriodicAuthorSearchValue,
    setAreaData: setPeriodicAreaData,
    setAuthorData: setPeriodicAuthorData,
    setDateData: setPeriodicDateData,
    setOrder: setPeriodicOrder,
    setPage: setPeriodicPage,
    areaData: areaPeriodicData,
    authorData: authorPeriodicData,
    dateData: datePeriodicData
  } = useContext(PeriodicFiltersContext);

  const {
    updateData: updateCodesData,
    setOrder: setCodesOrder,
    setPage: setCodesPage
  } = useContext(CodesFiltersContext);

  const {
    updateData: updateInformativesData,
    setDateData: setInformativeDateData,
    setOrder: setInformativesOrder,
    setPage: setInformativesPage,
    dateData: dateInformativesData
  } = useContext(InformativesFiltersContext);

  const getSearchString = () =>
    String(searchString || searchParam || wordsParam || '').trim();

  const doSearchGlobal = (search: string) => {
    const url = getUrlParamsForSearch('/pesquisa/todos', {
      search,
      searchScope: searchScope || '0',
      sortBy: getValidSortBy(sortParam)
    });
    // Só muda para 'relevance' se houver uma busca ativa (texto digitado)
    if (search && search.trim().length > 0) {
      setGlobalOrder('relevance');
    } else {
      setGlobalOrder('date:desc');
    }
    if (!advancedGlobalData) {
      setAdvancedGlobalData(processAdvancedSearch(search, searchScope));
    }

    updateGlobalData();
    setGlobalPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchVideo = (search: string) => {
    const url = getUrlParamsForSearch('/conteudo/videos', {
      search,
      searchScope: searchScope || '0',
      sortBy: getValidSortBy(sortParam)
    });
    setVideoOrder('relevance');

    updateVideoData();
    setVideoPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchBook = (search: string) => {
    const url = getUrlParamsForSearch('/conteudo/livros', {
      search,
      searchScope: scopeParam || '0',
      sortBy: getValidSortBy(sortParam)
    });
    setBookOrder('relevance');
    updateBookData();
    setBookPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchPeriodicV2 = (search: string) => {
    const url = getUrlParamsForSearch('/conteudo/revistas', {
      search,
      searchScope: scopeParam || '0',
      sortBy: getValidSortBy(sortParam)
    });
    setPeriodicOrder('relevance');
    updatePeriodicData();
    setPeriodicPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchCodesV2 = (search: string) => {
    const url = getUrlParamsForSearch('/conteudo/codigos', {
      search,
      searchScope: scopeParam || '0',
      sortBy: getValidSortBy(sortParam)
    });
    setCodesOrder('relevance');
    updateCodesData();
    setCodesPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchInformativesV2 = (search: string) => {
    const url = getUrlParamsForSearch('/conteudo/informativos', {
      search,
      searchScope: scopeParam || '0',
      sortBy: getValidSortBy(sortParam)
    });
    setInformativesOrder('relevance');
    updateInformativesData();
    setInformativesPage(1);
    setSearchCount(searchCount + 1);
    setTimeout(() => goTo(history, url), 1);
  };

  const doSearchPeriodic = (search: string) => {
    const filter = new SearchFilter(search);
    filter.searchScope = searchScope;
    filter.words = search;
    filter.page = 0;
    setPeriodicPage(1);
    setSearchCount(searchCount + 1);
    changeFilter(filter);
  };

  const doSearch = (page: string, search: string, searchScope: number) => {
    window.scrollTo({ top: 0 });

    if (!searchCount && ignoreSearchOnFirstAccess) {
      return setSearchCount(searchCount + 1);
    }

    if (!periodicRoutes.includes(page)) {
      filter.searchScope = searchScope;
      filter.words = search;
      filter.page = 0;
      changeFilter(filter);
    }

    if (page === 'search') return doSearchGlobal(search);
    if (videoRoutes.includes(page)) return doSearchVideo(search);
    if (!isNewPeriodicVers && periodicRoutes.includes(page))
      return doSearchPeriodic(search);
    if (isNewBookVers && bookRoutes.includes(page)) return doSearchBook(search);
    if (isNewPeriodicVers && periodicRoutesV2.includes(page))
      return doSearchPeriodicV2(search);
    if (isNewCodesVers && codesRoutesV2.includes(page))
      return doSearchCodesV2(search);
    if (isNewInformativesVers && informativesRoutesV2.includes(page))
      return doSearchInformativesV2(search);
  };

  const handleSearch = () => {
    let search = getSearchString();
    const page = getActivePath(location.pathname);

    if (!searchCount && String(scopeParam) === '1') setSearchScope(1);

    if (searchCount && !searchString) {
      setSearchScope(0);
      search = '';
    }

    doSearch(page, search, searchScope);
  };

  const checkFalse = (e) => ({ ...e, checked: false });
  const checkRangeFalse = (e) => ({ ...e, start: false, end: false });

  const clearFiltersGlobal = () => {
    setGlobalTypeSearchValue('');
    setGlobalAreaSearchValue('');
    setGlobalAuthorSearchValue('');
    setAdvancedGlobalData([{ type: 'AND', text: '', exact: false }]);
    setGlobalTypeData(typeGlobalData.map(checkFalse));
    setGlobalAreaData(areaGlobalData.map(checkFalse));
    setGlobalAuthorData(authorGlobalData.map(checkFalse));
    setGlobalDateData(dateGlobalData.map(checkRangeFalse));

    setEventGlobalData(eventGlobalData.map(checkFalse));
    setAppTypeGlobalData(appTypeGlobalData.map(checkFalse));
    setBookGlobalData(bookGlobalData.map(checkFalse));
    setPeriodicGlobalData(periodicGlobalData.map(checkFalse));
    setSerieBookGlobalData(serieBookGlobalData.map(checkFalse));
    setSerieVideoGlobalData(serieVideoGlobalData.map(checkFalse));

    setGlobalPage(1);
    setGlobalOrder('date:desc');
  };

  const clearFiltersPeriodic = () => {
    setPeriodicAreaSearchValue('');
    setPeriodicAuthorSearchValue('');
    setPeriodicAreaData(areaPeriodicData.map(checkFalse));
    setPeriodicAuthorData(authorPeriodicData.map(checkFalse));
    setPeriodicDateData(datePeriodicData.map(checkRangeFalse));
    setPeriodicPage(1);
    setPeriodicOrder('date:desc');
  };

  const clearFiltersBook = () => {
    setBookAreaSearchValue('');
    setBookAuthorSearchValue('');
    setBookSerieSearchValue('');
    setBookAreaData(areaBookData.map(checkFalse));
    setBookAuthorData(authorBookData.map(checkFalse));
    setBookSerieData(serieBookData.map(checkFalse));
    setBookDateData(dateBookData.map(checkRangeFalse));
    setBookPage(1);
    setBookOrder('date:desc');
  };

  const clearFiltersVideo = () => {
    setVideoAreaSearchValue('');
    setVideoAuthorSearchValue('');
    setVideoEventSearchValue('');
    setVideoSerieSearchValue('');
    setVideoAreaData(areaVideoData.map(checkFalse));
    setVideoAuthorData(authorVideoData.map(checkFalse));
    setVideoEventData(eventVideoData.map(checkFalse));
    setVideoSerieData(serieVideoData.map(checkFalse));
    setVideoDateData(dateVideoData.map(checkRangeFalse));
    setVideoPage(1);
    setVideoOrder('date:desc');
  };

  const clearFiltersCodes = () => {
    setCodesOrder('relevance');
    setCodesPage(1);
  };

  const clearFiltersInformatives = () => {
    setInformativeDateData(dateInformativesData.map(checkRangeFalse));
    setInformativesOrder('relevance');
    setInformativesPage(1);
  };

  const match = useRouteMatch();
  const clearFilters = () => {
    const [, pathPart1, pathPart2] = match.url.split('/');

    clearFiltersGlobal();
    clearFiltersPeriodic();
    clearFiltersBook();
    clearFiltersVideo();
    clearFiltersCodes();
    clearFiltersInformatives();

    setTimeout(() => {
      history.push(`/${pathPart1}/${pathPart2}`);
      updateGlobalData();
    }, 250);
  };

  useEffect(() => {
    handleSearch();
  }, [searchScope]);

  useEffect(() => {
    setSearchString(getSearchString());
  }, []);

  const scopeOptions = [
    { value: 0, label: 'Em todo conteúdo' },
    { value: 1, label: 'Somente no título' }
  ];

  const [questionVisible, setQuestionVisible] = useState(false);
  const contentQuestion = (
    <div>
      <div style={{ textAlign: 'center' }}>
        <h5>
          <span>
            A amplitude "<b>Somente no título</b>" contempla títulos de{' '}
            <b>livros</b>, <b>capítulos</b>, <b>revistas</b>, <b>doutrinas</b>,{' '}
            <b>jurisprudências</b> e <b>vídeos</b>.
          </span>
        </h5>
      </div>
    </div>
  );

  const [infoVisible, setInfoVisible] = useState(false);
  const contentInfo = (
    <div>
      <div style={{ textAlign: 'center' }}>
        <h5>
          <span>
            Não é mais necessário utilizar <b>comandos booleanos</b>!
            <br />
            Você pode personalizar sua pesquisa com os <b>
              filtros avançados
            </b>{' '}
            da plataforma.
          </span>
        </h5>
      </div>
    </div>
  );

  return (
    <Container id="element-3" className={searchOpen ? 'opened' : 'closed'}>
      <Wrapper id="advanced-bar">
        {/* <div
          className="search-description"
          style={{
            color: 'white',
            marginTop: 12,
            marginRight: 10,
            fontSize: 12
          }}>
          Personalize sua pesquisa:{' '}
          <Icon
            type="question-circle"
            style={{ fontSize: 14, cursor: 'pointer' }}
            onClick={() => {
              setInfoVisible(!infoVisible);
            }}
          />
        </div> */}
        {/* <IconSearch src={lupaIcon} alt="" /> */}
        <DivDot>
          <SpanDotPink />
          <SpanDotGreen />
        </DivDot>
        <Popover
          style={{ zIndex: 1031 }}
          trigger="contextMenu"
          placement="bottom"
          visible={infoVisible}
          content={contentInfo}
          onVisibleChange={() => {
            setInfoVisible(!infoVisible);
          }}
          getPopupContainer={(trigger) => trigger}>
          <GlobalNavBarAdvanced
            className="global-navbar-advanced"
            style={{
              width: (advancedGlobalData || []).length > 1 ? 642 : 632,
              padding: 5,
              background: loadingGlobal ? '#F5F5F5' : undefined
            }}>
            <GlobalAdvancedFilter
              handleSearch={handleSearch}
              showBooleanInfo={() => {
                setInfoVisible(true);
              }}
            />
          </GlobalNavBarAdvanced>
        </Popover>
        <SearchBreadth>
          <span
            style={{
              fontWeight: 'bold'
            }}>
            Amplitude da busca:{' '}
          </span>
          <Select
            disabled={loadingGlobal}
            optionLabelProp="title"
            filterOption={false}
            onSelect={(v: SearchScopeType) => setSearchScope(v)}
            value={searchScope}>
            <Option value={0} title={'Em todo conteúdo'}>
              {'Em todo conteúdo'}
            </Option>
            <Option value={1} title={'Somente no título'}>
              {'Somente no título'}
            </Option>
          </Select>
          <Popover
            style={{ zIndex: 1031 }}
            trigger="click"
            placement="bottom"
            visible={questionVisible}
            content={contentQuestion}
            onVisibleChange={() => {
              setQuestionVisible(!questionVisible);
            }}
            getPopupContainer={(trigger) => trigger}>
            <Icon
              type="question-circle"
              style={{ marginLeft: 10, fontSize: 14, cursor: 'pointer' }}
            />
          </Popover>
          <SpanClearAllInternal onClick={clearFilters}>
            <Icon type="delete" style={{ fontSize: 20 }} className="pointer" />{' '}
            Limpar tudo
          </SpanClearAllInternal>
        </SearchBreadth>
        <SpanDivider>|</SpanDivider>
        <SpanClearAll onClick={clearFilters}>
          <Icon type="delete" style={{ fontSize: 20 }} className="pointer" />{' '}
          Limpar tudo
        </SpanClearAll>
      </Wrapper>
      <Wrapper>
        <div
          style={{
            color: 'white',
            textAlign: 'center',
            marginTop: 12,
            fontSize: 12,
            width: '100%'
          }}>
          {/* <span style={{ fontWeight: 'bold' }}>Amplitude: </span>
          <RadioGroup
            onChange={(e: any) => setSearchScope(e.target.value)}
            options={scopeOptions}
            value={searchScope}
          /> */}

          <div className="sep" />
        </div>
      </Wrapper>
    </Container>
  );
};
